import { defineStore } from 'pinia'
import { apiService } from '@/services/api'

export const useBranchStore = defineStore('branch', {
  state: () => ({
    branches: [],
    tableList: [],
    formData: null,
    formInit: {
      id: null,
      companyId: null,
      rowNumber: 0,
      name: '',
      nameKana: '',
      postalCode: ['', ''],
      prefecture: '',
      prefectureCode: '',
      prefectureKana: '', // 追加
      address: '',
      addressKana: '',
      phone: ['', '', ''],
      fax: ['', '', ''],
      email: '',
      industry: '',
      representativeLastName: '',
      representativeFirstName: '',
      representativeLastKana: '',
      representativeFirstKana: '',
      representativeTitle: '',
      representativeDivision: '',
      representativeDivisionKana: '',
      laborBureauCode: '',
      laborOfficeCode: '',
      laborBasicNumber: '',
      laborBranchNumber: '',
      laborConsolidatedNumber: '',
      hasLaborInsuranceRegular: true,
      hasLaborInsuranceExecutive: true,
      hasLaborInsuranceTemporary: true,
      memo: '',
    },
    selectedBranch: null,
    currentCompanyId: null,
    branchOptions: [],
  }),
  actions: {
    // テーブル表示用のリストを初期化
    initTableList() {
      this.tableList = [
        { label: 'No', key: 'rowNumber', data: (item, value) => value },
        { label: '事業所名称', key: 'name', data: (item, value) => value },
        { label: '都道府県', key: 'prefecture', data: (item, value) => value },
        { label: '従業員数', key: 'staffCount', data: (item, value) => value || 0 },
        { label: '労働局', key: 'laborBureau', data: (item, value) => (value ? `${value}` : '') },
        { label: '労働基準監督署', key: 'laborOffice', data: (item, value) => (value ? `${value}` : '') },
        // {
        //   label: '労災保険加入区分',
        //   key: 'laborInsuranceTypes',
        //   data: (item, value) => value,
        // },
        {
          label: '従業員',
          key: 'staff',
          data: (item, value) => '従業員一覧へ',
          action: (table, item, event) => {
            event.stopPropagation()
            this.selectedBranch = item.id
          },
          actionEnabled: (table, item) => true, // 常に有効
          css: 'btn',
        },
        {
          label: 'メモ',
          key: 'memo',
          data: (item, value) => {
            return value?.length > 20 ? value.substring(0, 20) + '...' : value
          },
        },
      ]
    },

    // フォームデータの初期化
    async setFormData(id) {
      if (id) {
        // 編集の場合
        const branch = await this.getBranch(id)
        if (branch) {
          this.formData = JSON.parse(JSON.stringify(branch))
          return
        }
      }
      // 新規作成の場合
      this.clearFormData()
      // 会社IDをセット
      if (this.currentCompanyId) {
        this.formData.companyId = this.currentCompanyId
      }
    },

    // 会社IDに紐づく事業所一覧を取得
    async getBranches(companyId) {
      try {
        this.currentCompanyId = companyId
        const response = await apiService.get(`/api/companies/${companyId}/branches`)
        this.branches = response.branches
        return true
      } catch (error) {
        return false
      }
    },

    // 事業所の情報を取得
    async getBranch(id) {
      try {
        const response = await apiService.get(`/api/companies/${this.currentCompanyId}/branches/${id}`)
        return response.branch
      } catch (error) {
        return false
      }
    },

    // 事業所情報を保存（新規作成・更新）
    async saveBranch() {
      try {
        const response = await apiService.post(`/api/companies/${this.currentCompanyId}/branches`, this.formData)
        await this.getBranches(this.currentCompanyId)
        return response.message
      } catch (error) {
        return null
      }
    },

    // 事業所を削除
    async deleteBranch() {
      try {
        const response = await apiService.delete(`/api/companies/${this.currentCompanyId}/branches/${this.formData.id}`)
        await this.getBranches(this.currentCompanyId)
        return response.message
      } catch (error) {
        return null
      }
    },

    // 事業場の名前一覧を取得
    async getBranchOptions(companyId) {
      try {
        this.currentCompanyId = companyId
        const response = await apiService.get(`/api/companies/${this.currentCompanyId}/branch-names`)
        this.branchOptions = response.branches.map(branch => ({
          value: branch.id,
          label: branch.name,
        }))
        return true
      } catch (error) {
        return false
      }
    },

    // IDで事業所を検索
    getBranchById(id) {
      return this.branches.find(branch => branch.id === Number(id))
    },

    // 事業所情報をコピー（新規作成時の雛形として）
    copyBranch() {
      this.formData.id = null
    },

    // フォームデータをクリア
    clearFormData() {
      this.formData = JSON.parse(JSON.stringify(this.formInit))
    },

    // 郵便番号が入力されているかチェック
    isPostalCodeFilled() {
      // 0番目が3桁、1番目が4桁の配列であることを確認
      return this.formData.postalCode[0].length === 3 && this.formData.postalCode[1].length === 4
    },

    // 住所をセット
    setAddress(address) {
      if (!address) {
        return
      }
      this.formData.prefecture = address.prefecture

      // 都道府県カナをセット
      this.formData.prefectureKana = address.prefectureKana || ''

      this.formData.address = address.address1

      // 住所カナをセット（半角数字を全角数字に変換）
      if (address.address1Kana) {
        this.formData.addressKana = address.address1Kana.replace(/[0-9]/g, function (s) {
          return String.fromCharCode(s.charCodeAt(0) + 0xfee0)
        })
      } else {
        this.formData.addressKana = address.address1Kana || ''
      }

      this.formData.prefectureCode = address.prefectureCode
    },

    // 従業員一覧へ遷移
    navigateToStaff(router) {
      if (this.selectedBranch) {
        router.push({ name: 'staff', query: { branchId: this.selectedBranch } })
      }
    },

    // オプションのラベルを取得
    getBranchOptionLabel(id) {
      const option = this.branchOptions.find(option => option.value == id)
      return option ? option.label : ''
    },
  },
  getters: {
    postalCodeWithHyphen(state) {
      return state.formData.postalCode[0] + '-' + state.formData.postalCode[1]
    },
  },
})
